import { useEffect, useRef } from 'react';

/**
 * useAutosave — debounced autosave hook replacing react-autosave.
 *
 * Fires onSave(data) after `interval` ms of stability whenever `data` changes.
 * Skips the very first render and skips saves when the serialized data is
 * identical to the last value that was actually saved, matching the behaviour
 * of react-autosave which used shallow/JSON equality to avoid redundant writes.
 *
 * @param {*}        data      The value to watch (any JSON-serialisable value).
 * @param {Function} onSave    Called with `data` when the debounce fires.
 * @param {number}   [interval=1000]  Debounce delay in milliseconds.
 * @param {boolean}  [enabled=true]   Pass false to disable (replaces conditional render).
 */
export function useAutosave(
  data: any,
  onSave: (data: any) => void,
  interval = 1000,
  enabled = true,
) {
  // Always track the latest onSave so stale closures never fire an old handler.
  const savedCallback = useRef(onSave);
  useEffect(() => {
    savedCallback.current = onSave;
  });

  // Track whether this is the very first render.
  const isFirstRender = useRef(true);

  // Track the last serialized value that was actually saved so we can skip
  // redundant saves when the data hasn't changed between debounce windows.
  const lastSavedSerialized = useRef(undefined);

  useEffect(() => {
    // Skip the initial mount — we never want to save on first render.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Seed the last-saved ref with the initial serialized value so the very
      // first real change will always trigger a save.
      lastSavedSerialized.current = JSON.stringify(data);
      return;
    }

    if (!enabled) {
      return;
    }

    const serialized = JSON.stringify(data);

    // Bail out if the data hasn't meaningfully changed since the last save.
    if (serialized === lastSavedSerialized.current) {
      return;
    }

    const timer = setTimeout(() => {
      lastSavedSerialized.current = serialized;
      savedCallback.current(data);
    }, interval);

    return () => clearTimeout(timer);
  }, [data, interval, enabled]);
}

/**
 * AutosaveEffect — drop-in JSX replacement for <Autosave />.
 *
 * Renders nothing; exists only to call useAutosave inside a valid React
 * component context (render props, conditional subtrees, etc.).
 *
 * Usage: <AutosaveEffect data={values} onSave={handleSubmit} interval={200} />
 */
interface AutosaveEffectProps {
  data: any;
  onSave: (data: any) => void;
  interval?: number;
  enabled?: boolean;
}

export function AutosaveEffect({
  data,
  onSave,
  interval = 1000,
  enabled = true,
}: AutosaveEffectProps) {
  useAutosave(data, onSave, interval, enabled);
  return null;
}
